/**
 * AuthContext — global auth state for the Chat frontend.
 *
 * On mount it tries a silent refresh (the HttpOnly cookie is sent
 * automatically).  If that succeeds the user is "logged in" without
 * ever touching localStorage.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import api, { setAccessToken, clearAccessToken } from '../services/api';
import { setTelemetryUser, clearTelemetryUser } from '../services/telemetry';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // UserResponse from backend
  const [loading, setLoading] = useState(true);  // initial silent-refresh in progress

  // Keep RUM identity in step with auth state. Done as an effect on `user`
  // rather than at each login/logout call site — there are five of them
  // (login, register, two OAuth paths, silent refresh) and one missed call
  // would silently attribute a session to the wrong user.
  useEffect(() => {
    if (user?.id) {
      setTelemetryUser(user.id);
    } else {
      clearTelemetryUser();
    }
  }, [user]);

  // ── helpers ───────────────────────────────────────────────────────────

  /** Try silent refresh (cookie → new access token + user). */
  const silentRefresh = useCallback(async () => {
    try {
      const { data } = await api.post('/auth/refresh');
      // An anonymous visitor has no refresh cookie and gets a plain
      // "not signed in" rather than a 401 — being signed out is a normal
      // state, not a failure. Only a real token grants a session.
      if (!data?.access_token) {
        clearAccessToken();
        setUser(null);
        return false;
      }
      setAccessToken(data.access_token);
      setUser(data.user);
      return true;
    } catch {
      clearAccessToken();
      setUser(null);
      return false;
    }
  }, []);

  /** Standard email / password login. */
  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setAccessToken(data.access_token);
    setUser(data.user);
    return data;
  }, []);

  /** Register (local). */
  const register = useCallback(async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    return data; // { message: "..." }
  }, []);

  /* OAuth redirect flow — exchange the authorization code.
   *
   * `state` is passed straight through to the server, which verifies it and
   * answers with `return_to`: the host the sign-in began on, when that was a
   * company advisor subdomain rather than this one. We never read a destination
   * out of `state` here — it arrives via the provider, so trusting it client-
   * side would make this an open redirect. See services/oauthRelay.js. */

  const googleCodeSignIn = useCallback(async (code, redirectUri, state = '') => {
    const { data } = await api.post('/auth/oauth/google/callback',
      { code, redirect_uri: redirectUri, state });
    setAccessToken(data.access_token);
    setUser(data.user);
    return data;
  }, []);

  const facebookCodeSignIn = useCallback(async (code, redirectUri, state = '') => {
    const { data } = await api.post('/auth/oauth/facebook/callback',
      { code, redirect_uri: redirectUri, state });
    setAccessToken(data.access_token);
    setUser(data.user);
    return data;
  }, []);

  /** Logout — revoke refresh token + clear state. */
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* best-effort */
    }
    clearAccessToken();
    setUser(null);
  }, []);

  /** Forgot password */
  const forgotPassword = useCallback(async (email) => {
    const { data } = await api.post('/auth/forgot-password', { email });
    return data;
  }, []);

  /** Reset password */
  const resetPassword = useCallback(async (token, newPassword, confirmPassword) => {
    const { data } = await api.post('/auth/reset-password', {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    return data;
  }, []);

  /** Verify email */
  const verifyEmail = useCallback(async (token) => {
    const { data } = await api.post('/auth/verify-email', { token });
    return data;
  }, []);

  // ── on mount: try silent refresh ──────────────────────────────────────
  useEffect(() => {
    silentRefresh().finally(() => setLoading(false));
  }, [silentRefresh]);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      isAdmin: user?.roles?.includes('admin') ?? false,
      login,
      register,
      googleCodeSignIn,
      facebookCodeSignIn,
      logout,
      forgotPassword,
      resetPassword,
      verifyEmail,
      silentRefresh,
      setUser,
    }),
    [user, loading, login, register, googleCodeSignIn, facebookCodeSignIn, logout, forgotPassword, resetPassword, verifyEmail, silentRefresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

export default AuthContext;
